import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge, Box, Button, Card, CardBody, CardHeader, Flex, HStack, Heading,
  IconButton, Input, Select, SimpleGrid, Table, Tbody, Td, Text, Th, Thead,
  Tr, VStack, useToast,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import PageShell from '../components/PageShell';
import apiClient from '../services/apiClient';

/**
 * Per-OTA rate modifiers, applied by the channel manager itself.
 *
 * Aiosell: its channel_multiplier API scales every pushed rate by a
 * factor per channel — percentages only. Channex: connection-level
 * derived_option rules — percent or fixed amount. Saving replaces the
 * configuration's modifier set and applies it immediately; each row
 * reports whether the channel manager accepted it.
 */
const RateModifiers = () => {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Per-config editor state: { [configId]: { meta, rows, saving } }
  const [editors, setEditors] = useState({});
  const toast = useToast();

  const loadEditor = useCallback(async (configId) => {
    const data = await apiClient.get(
      `/channel-manager/configurations/${configId}/rate-modifiers/`);
    setEditors((prev) => ({
      ...prev,
      [configId]: {
        meta: data,
        rows: (data.modifiers || []).map((m) => ({ ...m })),
        saving: false,
      },
    }));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient.get('/channel-manager/configurations/');
      const list = data.configurations || data.configs || data || [];
      setConfigs(Array.isArray(list) ? list : []);
      await Promise.all(
        (Array.isArray(list) ? list : []).map((c) => loadEditor(c.id))
      );
    } catch (err) {
      setError(err.response?.data?.detail || err.message || t('rateModifiers.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [loadEditor, t]);

  useEffect(() => { load(); }, [load]);

  const updateRow = (configId, index, patch) => {
    setEditors((prev) => {
      const editor = prev[configId];
      if (!editor) return prev;
      const rows = editor.rows.map((row, i) =>
        (i === index ? { ...row, ...patch } : row));
      return { ...prev, [configId]: { ...editor, rows } };
    });
  };

  const addRow = (configId) => {
    setEditors((prev) => {
      const editor = prev[configId];
      if (!editor) return prev;
      return {
        ...prev,
        [configId]: {
          ...editor,
          rows: [...editor.rows, {
            channel: '', modifierType: 'percent', value: 0, isActive: true,
          }],
        },
      };
    });
  };

  const removeRow = (configId, index) => {
    setEditors((prev) => {
      const editor = prev[configId];
      if (!editor) return prev;
      return {
        ...prev,
        [configId]: {
          ...editor,
          rows: editor.rows.filter((_, i) => i !== index),
        },
      };
    });
  };

  const save = async (configId) => {
    const editor = editors[configId];
    if (!editor) return;
    setEditors((prev) => ({
      ...prev, [configId]: { ...prev[configId], saving: true },
    }));
    try {
      const payload = {
        modifiers: editor.rows
          .filter((r) => String(r.channel || '').trim())
          .map((r) => ({
            channel: String(r.channel).trim(),
            modifierType: r.modifierType,
            value: parseFloat(r.value) || 0,
            isActive: r.isActive !== false,
          })),
      };
      const data = await apiClient.put(
        `/channel-manager/configurations/${configId}/rate-modifiers/`,
        payload);
      const result = data.result || {};
      toast({
        title: t('rateModifiers.toasts.applied'),
        description: t('rateModifiers.toasts.appliedSummary', { applied: result.applied || 0, failed: result.failed || 0 }),
        status: result.failed ? 'warning' : 'success',
        duration: 4000,
      });
      await loadEditor(configId);
    } catch (err) {
      toast({
        title: t('rateModifiers.toasts.saveFailed'),
        description: err.response?.data?.detail || err.message,
        status: 'error',
        duration: 5000,
      });
      setEditors((prev) => ({
        ...prev, [configId]: { ...prev[configId], saving: false },
      }));
    }
  };

  const statusBadge = (row) => {
    if (!row.lastApplyStatus || row.lastApplyStatus === 'pending') return null;
    const ok = row.lastApplyStatus === 'applied';
    return (
      <Badge colorScheme={ok ? 'green' : 'red'} fontSize="2xs"
        title={row.lastApplyError || ''}>
        {ok ? t('rateModifiers.status.applied') : t('rateModifiers.status.failed')}
      </Badge>
    );
  };

  return (
    <PageShell
      title={t('rateModifiers.title')}
      subtitle={t('rateModifiers.subtitle')}
      breadcrumbs={[{ label: t('rateModifiers.title') }]}
      loading={loading}
      error={error}
    >
      <VStack spacing={6} align="stretch">
        {configs.length === 0 && !loading && (
          <Text color="gray.500">{t('rateModifiers.emptyConfigs')}</Text>
        )}
        {configs.map((config) => {
          const editor = editors[config.id];
          const meta = editor?.meta || {};
          const supportsFixed = !!meta.supportsFixed;
          const options = meta.channelOptions || [];
          return (
            <Card key={config.id} shadow="sm" borderWidth="1px">
              <CardHeader pb={2}>
                <Flex align="center" gap={2}>
                  <Heading size="sm">{config.name}</Heading>
                  <Badge>{config.channel_type}</Badge>
                  {!supportsFixed && (
                    <Text fontSize="xs" color="gray.400">
                      {t('rateModifiers.percentagesOnly')}
                    </Text>
                  )}
                </Flex>
              </CardHeader>
              <CardBody pt={0}>
                {!editor ? (
                  <Text fontSize="sm" color="gray.400">{t('rateModifiers.loading')}</Text>
                ) : (
                  <>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('rateModifiers.columns.channel')}</Th>
                          <Th w="140px">{t('rateModifiers.columns.type')}</Th>
                          <Th w="140px" isNumeric>{t('rateModifiers.columns.value')}</Th>
                          <Th w="110px">{t('rateModifiers.columns.status')}</Th>
                          <Th w="40px"></Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {editor.rows.map((row, i) => (
                          <Tr key={i}>
                            <Td>
                              {options.length > 0 ? (
                                <Select size="sm" value={row.channel}
                                  onChange={(e) => updateRow(config.id, i, { channel: e.target.value })}
                                  placeholder={t('rateModifiers.selectChannel')}>
                                  {/* keep an unknown stored value selectable */}
                                  {row.channel && !options.some((o) => o.value === row.channel) && (
                                    <option value={row.channel}>{row.channel}</option>
                                  )}
                                  {options.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                  ))}
                                </Select>
                              ) : (
                                <Input size="sm" value={row.channel}
                                  placeholder={t('rateModifiers.channelPlaceholder')}
                                  onChange={(e) => updateRow(config.id, i, { channel: e.target.value })} />
                              )}
                            </Td>
                            <Td>
                              <Select size="sm" value={row.modifierType}
                                onChange={(e) => updateRow(config.id, i, { modifierType: e.target.value })}>
                                <option value="percent">{t('rateModifiers.types.percent')}</option>
                                {supportsFixed && <option value="fixed">{t('rateModifiers.types.fixed')}</option>}
                              </Select>
                            </Td>
                            <Td isNumeric>
                              <Input size="sm" type="number" textAlign="right"
                                value={row.value}
                                onChange={(e) => updateRow(config.id, i, { value: e.target.value })} />
                            </Td>
                            <Td>{statusBadge(row)}</Td>
                            <Td>
                              <IconButton size="xs" variant="ghost" colorScheme="red"
                                aria-label={t('rateModifiers.buttons.remove')} icon={<DeleteIcon />}
                                onClick={() => removeRow(config.id, i)} />
                            </Td>
                          </Tr>
                        ))}
                        {editor.rows.length === 0 && (
                          <Tr><Td colSpan={5}>
                            <Text fontSize="sm" color="gray.400">
                              {t('rateModifiers.emptyRows')}
                            </Text>
                          </Td></Tr>
                        )}
                      </Tbody>
                    </Table>
                    <SimpleGrid columns={1}>
                      <HStack justify="space-between" mt={3}>
                        <Button size="xs" leftIcon={<AddIcon />} variant="outline"
                          onClick={() => addRow(config.id)}>
                          {t('rateModifiers.buttons.add')}
                        </Button>
                        <Button size="sm" colorScheme="blue"
                          isLoading={editor.saving}
                          onClick={() => save(config.id)}>
                          {t('rateModifiers.buttons.saveApply')}
                        </Button>
                      </HStack>
                      <Text fontSize="xs" color="gray.400" mt={2}>
                        {t('rateModifiers.help')}
                      </Text>
                    </SimpleGrid>
                  </>
                )}
              </CardBody>
            </Card>
          );
        })}
      </VStack>
    </PageShell>
  );
};

export default RateModifiers;
